import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  emailTemplateApi,
} from "../api/emailTemplateApi";
import {
  EmailTemplate,
  UpdateEmailTemplateRequest,
  EmailTemplatePreview,
  PreviewEmailTemplateRequest,
} from "../types";
import { RootState } from "@/app/rootReducer";

interface EmailTemplateSettingsState {
  template: EmailTemplate | null;
  preview: EmailTemplatePreview | null;
  loading: boolean;
  saving: boolean;
  previewing: boolean;
  error: string | null;
  successMessage: string | null;
  isDirty: boolean;
}

const initialState: EmailTemplateSettingsState = {
  template: null,
  preview: null,
  loading: false,
  saving: false,
  previewing: false,
  error: null,
  successMessage: null,
  isDirty: false,
};

// Async thunks
export const fetchEmailTemplate = createAsyncThunk(
  "emailTemplateSettings/fetchTemplate",
  async () => {
    const template = await emailTemplateApi.getTemplate();
    return template;
  }
);

export const updateEmailTemplate = createAsyncThunk(
  "emailTemplateSettings/updateTemplate",
  async (template: UpdateEmailTemplateRequest) => {
    const updated = await emailTemplateApi.updateTemplate(template);
    return updated;
  }
);

export const previewEmailTemplate = createAsyncThunk(
  "emailTemplateSettings/previewTemplate",
  async (request: PreviewEmailTemplateRequest) => {
    const preview = await emailTemplateApi.previewTemplate(request);
    return preview;
  }
);

export const resetEmailTemplate = createAsyncThunk(
  "emailTemplateSettings/resetTemplate",
  async () => {
    const template = await emailTemplateApi.resetToDefault();
    return template;
  }
);

const emailTemplateSettingsSlice = createSlice({
  name: "emailTemplateSettings",
  initialState,
  reducers: {
    setIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearPreview: (state) => {
      state.preview = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch template
    builder
      .addCase(fetchEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.template = action.payload;
        state.isDirty = false;
      })
      .addCase(fetchEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch email template";
      });

    // Update template
    builder
      .addCase(updateEmailTemplate.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateEmailTemplate.fulfilled, (state, action) => {
        state.saving = false;
        state.template = action.payload;
        state.successMessage = "Email template updated successfully";
        state.isDirty = false;
      })
      .addCase(updateEmailTemplate.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || "Failed to update email template";
      });

    // Preview template
    builder
      .addCase(previewEmailTemplate.pending, (state) => {
        state.previewing = true;
        state.error = null;
      })
      .addCase(previewEmailTemplate.fulfilled, (state, action) => {
        state.previewing = false;
        state.preview = action.payload;
      })
      .addCase(previewEmailTemplate.rejected, (state, action) => {
        state.previewing = false;
        state.error = action.error.message || "Failed to preview email template";
      });

    // Reset template
    builder
      .addCase(resetEmailTemplate.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(resetEmailTemplate.fulfilled, (state, action) => {
        state.saving = false;
        state.template = action.payload;
        state.successMessage = "Email template reset to default";
        state.isDirty = false;
        state.preview = null;
      })
      .addCase(resetEmailTemplate.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || "Failed to reset email template";
      });
  },
});

export const {
  setIsDirty,
  clearError,
  clearSuccessMessage,
  clearPreview,
} = emailTemplateSettingsSlice.actions;

// Selectors
export const selectEmailTemplate = (state: RootState) => state.emailTemplateSettings.template;
export const selectEmailPreview = (state: RootState) => state.emailTemplateSettings.preview;
export const selectIsLoading = (state: RootState) => state.emailTemplateSettings.loading;
export const selectIsSaving = (state: RootState) => state.emailTemplateSettings.saving;
export const selectIsPreviewing = (state: RootState) => state.emailTemplateSettings.previewing;
export const selectIsDirty = (state: RootState) => state.emailTemplateSettings.isDirty;

export default emailTemplateSettingsSlice.reducer;